// One place that knows how to open a Lemon Squeezy checkout from inside the app.
//
// Why this exists: on 2026-08-26 the entire logged-in client contained exactly
// THREE `openOverlay()` call sites — the trial banner (TRIALING users only),
// the public pricing page, and one public tool page. Every other "Upgrade"
// control in the product was a *navigation* to /pricing or to /settings/billing
// (itself a redirect to /settings#billing whose own buttons full-page-reloaded
// back to /pricing). The 89 users whose trial had expired had no control
// anywhere that could open a checkout, and `checkout_started` stood at 2
// all-time against 182 users.
//
// Every surface now goes through this hook so that six things can never be
// forgotten again:
//   1. the checkout URL comes from GET /billing/config, never from a
//      `VITE_LS_*_VARIANT_ID` env value ([QA-K]: those are LS-internal DB ids
//      and 404 on the public checkout endpoint);
//   2. `onSuccess` routes the buyer to /billing/return, which is the only
//      thing that re-polls /user and renders the post-upgrade interstitial —
//      the trial banner omitted it and left buyers on a stale cached tier;
//   3. a URL that carries no `checkout[custom][user_id]` is never opened for a
//      signed-in user (the webhook has nothing else to attribute the payment
//      to — the buyer would be charged and stay FREE);
//   4. when no URL resolves, the control degrades to a /pricing link instead
//      of becoming a dead click;
//   5. the checkout is bound to the EXACT variant the buyer picked (weekly /
//      monthly / yearly) via `POST /billing/checkouts`, never the bare static
//      URL — Lemon Squeezy's hosted checkout ignores the `?variant=` query
//      param on those and defaults to Monthly (verified live 2026-09-21: a
//      $7.99/week click was charged $19.99/month). `requestCheckoutUrl` below
//      does this;
//   6. on a weekly/yearly checkout-API failure, the control NEVER falls back
//      to the static URL — that would silently recreate the exact defect #5
//      describes (Codex review 2026-09-21). Only `monthly` may fall back,
//      because that is also what LS's hosted page defaults to on its own.
import { t } from "@lingui/macro";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { rememberCheckoutAttempt } from "@/client/services/billing/last-checkout-attempt";
import { useUser } from "@/client/services/user";
import { closePaywall } from "@/client/stores/paywall";

import type { BillingConfig } from "./use-billing-config";
import { BILLING_CONFIG_KEY, useBillingConfig } from "./use-billing-config";
import { useLemonSqueezyOverlay } from "./use-lemon-squeezy-overlay";

export type UpgradeTier = "PRO" | "ULTIMATE";
export type UpgradeInterval = "weekly" | "monthly" | "yearly";

/**
 * - `ready`       — a stamped checkout URL is in hand; the control opens checkout.
 * - `loading`     — /billing/config is still in flight (or is being re-fetched
 *                   because the cached copy predates sign-in); the control still
 *                   works, it just links to /pricing for now.
 * - `unavailable` — billing is not configured server-side, or the request
 *                   failed; the control permanently links to /pricing.
 */
export type UpgradeCheckoutState = "ready" | "loading" | "unavailable";

/** LS custom-data param the billing webhook uses to find the buyer's account. */
const USER_ID_PARAM = "checkout[custom][user_id]";

/** `checkout_open_result.code` when no checkout URL could be obtained (weekly/yearly fail closed). */
export const CHECKOUT_REQUEST_FAILED_CODE = "checkout_request_failed";

/** The only surface allowed to confirm a purchase — it polls /user for the webhook. */
const RETURN_PATH = "/billing/return";

/**
 * The tier of the checkout opened most recently, so the shared success handler
 * can stamp `?plan=` for the Google Ads conversion on /billing/return.
 *
 * Module-level on purpose: `window.LemonSqueezy.Setup()` is a single global
 * slot, so with several mounted consumers only one `onSuccess` survives. Every
 * consumer therefore registers an *identical* handler and reads the tier from
 * here — whichever instance wins the race behaves the same way.
 */
let lastCheckoutTier: UpgradeTier | null = null;

export const resolveCheckoutUrl = (
  config: BillingConfig | null | undefined,
  tier: UpgradeTier,
  interval: UpgradeInterval,
): string | undefined => {
  if (!config) return undefined;

  if (tier === "ULTIMATE") {
    return interval === "yearly" ? config.ultimateYearlyCheckoutUrl : config.ultimateCheckoutUrl;
  }

  if (interval === "weekly") return config.proWeeklyCheckoutUrl;
  if (interval === "yearly") return config.proYearlyCheckoutUrl;
  return config.proCheckoutUrl;
};

/** True when the URL carries the custom-data param the webhook needs. */
export const hasUserAttribution = (checkoutUrl: string): boolean => {
  try {
    return new URL(checkoutUrl).searchParams.has(USER_ID_PARAM);
  } catch {
    return false;
  }
};

/**
 * Outcome of `requestCheckoutUrl`. `ok: false` means no URL can safely be
 * opened — the caller must show an error, never fall back to a guess.
 */
export type CheckoutRequestResult =
  | { ok: true; url: string; attemptId: string }
  | { ok: false; attemptId: string };

const stampAttempt = (url: string, attemptId: string): string => {
  const parsed = new URL(url);
  parsed.searchParams.set("checkout[custom][attempt_id]", attemptId);
  const success = parsed.searchParams.get("checkout[success_url]");
  if (success) {
    const redirect = new URL(success);
    redirect.searchParams.set("attempt_id", attemptId);
    parsed.searchParams.set("checkout[success_url]", redirect.toString());
  }
  return parsed.toString();
};

/**
 * `POST /billing/checkouts` and return the Lemon Squeezy Checkouts-API URL
 * bound to the exact (tier, interval) variant.
 *
 * `staticFallbackUrl` — the plain, decorated `/billing/config` URL the caller
 * already has in hand — is used ONLY when `interval === "monthly"` and the
 * request fails or returns no url. That fallback is genuinely safe for
 * monthly (it is also what Lemon Squeezy's hosted page defaults to on its
 * own), but NOT for weekly/yearly: serving it there would silently recreate
 * the exact defect this endpoint exists to fix — a $7.99/week click charged
 * at $19.99/month (verified live 2026-09-21, Codex review same day). For
 * `weekly`/`yearly` any failure resolves `{ ok: false }` instead, and the
 * caller must show an error rather than open an unbound checkout.
 */
export const requestCheckoutUrl = async (
  tier: UpgradeTier,
  interval: UpgradeInterval,
  staticFallbackUrl: string,
  mode: "overlay" | "hosted" = "hosted",
  surface = "unknown",
): Promise<CheckoutRequestResult> => {
  const attemptId = crypto.randomUUID();
  void axios
    .post("/analytics/track", {
      event: "checkout_started",
      props: { attempt_id: attemptId, surface, plan: tier, interval },
    })
    .catch(() => null);
  // No bound URL and no safe static fallback: the click ends here, so record the
  // failed open under the same attempt id as `checkout_started` (otherwise the
  // attempt has a start and no result, and reads as an abandoned cart).
  const reportRequestFailed = (): void => {
    void axios
      .post("/analytics/track", {
        event: "checkout_open_result",
        props: {
          attempt_id: attemptId,
          surface,
          plan: tier,
          interval,
          method: mode,
          ok: false,
          code: CHECKOUT_REQUEST_FAILED_CODE,
        },
      })
      .catch(() => null);
  };
  const fallback = (): CheckoutRequestResult => {
    if (interval === "monthly")
      return { ok: true, url: stampAttempt(staticFallbackUrl, attemptId), attemptId };
    reportRequestFailed();
    return { ok: false, attemptId };
  };

  try {
    const { data } = await axios.post<{ url?: string }>("/billing/checkouts", {
      tier,
      interval,
      mode,
      attemptId,
      surface,
    });
    return data.url ? { ok: true, url: data.url, attemptId } : fallback();
  } catch {
    return fallback();
  }
};

/**
 * Shared "couldn't start checkout" handling for every call site that uses
 * `requestCheckoutUrl` directly (the hook below, the pricing page's tier
 * cards). Centralised so the toast copy and the Sentry breadcrumb exist in
 * exactly one place.
 */
export const notifyCheckoutUnavailable = (tier: UpgradeTier, interval: UpgradeInterval): void => {
  // eslint-disable-next-line lingui/no-unlocalized-strings -- internal Sentry breadcrumb, never rendered to a user
  Sentry.captureMessage("Checkout API failed with no safe static fallback", {
    level: "warning",
    tags: { "checkout.tier": tier, "checkout.interval": interval },
  });
  toast({ variant: "error", title: t`Couldn't start checkout — please try again` });
};

type UseUpgradeCheckoutOptions = {
  tier?: UpgradeTier;
  interval?: UpgradeInterval;
  /** Attribution label, e.g. "sidebar" or "jobs_feed_preview". */
  source: string;
  /**
   * Defaults to the full hosted page. The embedded overlay runs the checkout in a
   * cross-site iframe whose cart never gets its X-Secret when third-party cookies
   * are blocked, so Pay returns Lemon Squeezy's 403 page. Do not pass "overlay"
   * for a buyer-facing checkout.
   */
  mode?: "overlay" | "hosted";
};

type UseUpgradeCheckoutResult = {
  state: UpgradeCheckoutState;
  /** Defined only when `state === "ready"`. */
  checkoutUrl?: string;
  /** Where the control points when no checkout URL is available. */
  pricingHref: string;
  /** Opens the selected checkout mode. No-op unless `state === "ready"`. */
  openCheckout: () => void;
  /** True while `POST /billing/checkouts` is in flight — show a busy state. */
  isCheckingOut: boolean;
  checkoutError: boolean;
};

/**
 * Registers the app-wide `Checkout.Success` handler. Mounted exactly once, by
 * `<CheckoutSuccessBridge/>` in `DialogProvider`.
 *
 * It deliberately does NOT live on the buttons themselves. A button can unmount
 * the instant it is clicked — the paywall dialog closes itself so the LS
 * overlay is not trapped behind Radix's `pointer-events: none` body lock — and
 * a handler that unmounts with it would be gone by the time the buyer finishes
 * paying, leaving them on a stale tier with no confirmation. That is exactly
 * the trial banner bug, one level up.
 */
export const useCheckoutSuccessRedirect = (): void => {
  const navigate = useNavigate();

  useLemonSqueezyOverlay({
    onSuccess: () => {
      // Entitlements are granted by the LS webhook — this is UX only. The
      // return page polls /user until the webhook lands, which is what makes
      // the new tier visible app-wide.
      if (globalThis.location.pathname === RETURN_PATH) return;
      closePaywall();
      // `success=true` is the app-controlled marker /billing/return requires
      // before it will poll /user/me at all — without it, a bare visit to
      // this path is indistinguishable from someone who never checked out.
      const params = new URLSearchParams({ success: "true" });
      if (lastCheckoutTier) params.set("plan", lastCheckoutTier.toLowerCase());
      void navigate(`${RETURN_PATH}?${params.toString()}`);
    },
  });
};

export const useUpgradeCheckout = ({
  tier = "PRO",
  interval = "monthly",
  source,
  mode = "hosted",
}: UseUpgradeCheckoutOptions): UseUpgradeCheckoutResult => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { data: billingConfig, isPending, isError } = useBillingConfig();
  const { openOverlay, openHosted, isHandoffInFlight } = useLemonSqueezyOverlay();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState(false);
  // A ref, not just the `isCheckingOut` state: state updates are batched and
  // a second click fired in the same tick as the first (double-click,
  // over-eager keyboard repeat) would still see the stale `false` — which is
  // exactly how two `checkout_started` events / two overlays happened.
  const isRequestingRef = useRef(false);
  // The account can arrive while the checkout request is in flight; the attempt
  // is tied to whoever is signed in when it is saved, not when it was clicked.
  const accountIdRef = useRef<string | undefined>();
  accountIdRef.current = user?.id;

  const resolvedUrl = resolveCheckoutUrl(billingConfig, tier, interval);

  // A `/billing/config` response fetched while logged out carries no
  // `checkout[custom][user_id]`, and the query is cached for 5 minutes and
  // shared with the anonymous /pricing page. Registering or signing in inside
  // the same SPA session would otherwise hand a signed-in user an unattributed
  // checkout. Re-fetch instead of opening it.
  const needsAttribution = Boolean(user?.id && resolvedUrl && !hasUserAttribution(resolvedUrl));

  useEffect(() => {
    if (!needsAttribution) return;
    void queryClient.invalidateQueries({ queryKey: BILLING_CONFIG_KEY });
  }, [needsAttribution, queryClient]);

  let state: UpgradeCheckoutState = "unavailable";
  if (isPending || needsAttribution) state = "loading";
  else if (resolvedUrl && !isError) state = "ready";

  const checkoutUrl = state === "ready" ? resolvedUrl : undefined;

  // UTM so a checkout opened from the fallback link is still attributable to
  // the surface the user clicked.
  const pricingParams = new URLSearchParams({
    billing: interval,
    utm_source: "app",
    utm_medium: source,
  });
  const pricingHref = `/pricing?${pricingParams.toString()}`;

  const openCheckout = useCallback(() => {
    if (!checkoutUrl || isRequestingRef.current || isHandoffInFlight?.()) return;
    isRequestingRef.current = true;
    setCheckoutError(false);
    lastCheckoutTier = tier;
    setIsCheckingOut(true);
    // Bind the checkout to the exact variant before opening it — the static
    // `checkoutUrl` is only the fallback `requestCheckoutUrl` degrades to,
    // and only for interval "monthly" (see its doc comment).
    void requestCheckoutUrl(tier, interval, checkoutUrl, mode, source)
      .then((result) => {
        if (result.ok) {
          // Both handoffs record `checkout_started` once through the shared hook.
          const analytics = { surface: source, plan: tier, interval, attempt_id: result.attemptId };
          if (mode === "hosted") {
            void Promise.resolve(openHosted(result.url, analytics)).then((opened) => {
              if (opened)
                rememberCheckoutAttempt({ tier, interval }, accountIdRef.current, {
                  mode: "hosted",
                  opened: true,
                });
            });
          } else {
            rememberCheckoutAttempt({ tier, interval }, accountIdRef.current, {
              mode: "overlay",
              opened: false,
            });
            openOverlay(result.url, analytics);
          }
        } else {
          setCheckoutError(true);
          notifyCheckoutUnavailable(tier, interval);
        }
      })
      .finally(() => {
        isRequestingRef.current = false;
        setIsCheckingOut(false);
      });
  }, [checkoutUrl, interval, isHandoffInFlight, mode, openHosted, openOverlay, source, tier]);

  return { state, checkoutUrl, pricingHref, openCheckout, isCheckingOut, checkoutError };
};
